// CreditNova Financial Calculations
// Using decimal.js for financial-grade precision

import { Decimal } from 'decimal.js';

// Configure decimal.js for financial precision
Decimal.set({
  precision: 28,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -9,
  toExpPos: 9,
});

// ===========================================
// Type Definitions
// ===========================================

// FinancialInputs is defined in schemas/index.ts, import from there if needed
export interface FinancialCalculationInputs {
  currentAssets?: string;
  currentLiabilities?: string;
  inventory?: string;
  totalDebt?: string;
  totalEquity?: string;
  intangibles?: string;
  operatingIncome?: string;
  depreciation?: string;
  amortization?: string;
  rent?: string;
  interest?: string;
  principal?: string;
  netIncome?: string;
  revenue?: string;
  grossProfit?: string;
  cogs?: string;
  avgInventory?: string;
  avgReceivables?: string;
  sales?: string;
  totalAssets?: string;
  retainedEarnings?: string;
  ebit?: string;
  workingCapital?: string;
  marketValueEquity?: string;
  totalLiabilities?: string;
}

export interface RatioResult {
  name: string;
  value: Decimal | null;
  formula: string;
  category: RatioCategory;
  benchmark?: { min?: number; max?: number; ideal?: number };
  interpretation?: string;
}

export type RatioCategory =
  | 'liquidity'
  | 'leverage'
  | 'profitability'
  | 'coverage'
  | 'activity'
  | 'cash_flow';

// ===========================================
// Helper Functions
// ===========================================

function safeDecimal(value: string | undefined): Decimal | null {
  if (!value || value.trim() === '') return null;
  try {
    const num = new Decimal(value.replace(/[,$]/g, ''));
    return num.isNaN() ? null : num;
  } catch {
    return null;
  }
}

function safeDivide(numerator: Decimal | null, denominator: Decimal | null): Decimal | null {
  if (!numerator || !denominator || denominator.isZero()) return null;
  return numerator.div(denominator);
}

// ===========================================
// Liquidity Ratios
// ===========================================

export const liquidityRatios = {
  /**
   * Current Ratio = Current Assets / Current Liabilities
   * Measures short-term liquidity
   * Benchmark: > 1.5 is healthy
   */
  currentRatio: (currentAssets: string, currentLiabilities: string): Decimal | null => {
    const ca = safeDecimal(currentAssets);
    const cl = safeDecimal(currentLiabilities);
    return safeDivide(ca, cl);
  },

  /**
   * Quick Ratio (Acid Test) = (Current Assets - Inventory) / Current Liabilities
   * More conservative liquidity measure
   * Benchmark: > 1.0 is healthy
   */
  quickRatio: (
    currentAssets: string,
    inventory: string,
    currentLiabilities: string
  ): Decimal | null => {
    const ca = safeDecimal(currentAssets);
    const inv = safeDecimal(inventory);
    const cl = safeDecimal(currentLiabilities);
    if (!ca || !cl) return null;
    const numerator = inv ? ca.minus(inv) : ca;
    return safeDivide(numerator, cl);
  },

  /**
   * Cash Ratio = Cash & Equivalents / Current Liabilities
   * Most conservative liquidity measure
   */
  cashRatio: (cashEquivalents: string, currentLiabilities: string): Decimal | null => {
    const cash = safeDecimal(cashEquivalents);
    const cl = safeDecimal(currentLiabilities);
    return safeDivide(cash, cl);
  },

  /**
   * Working Capital = Current Assets - Current Liabilities
   * Absolute measure of short-term liquidity
   */
  workingCapital: (currentAssets: string, currentLiabilities: string): Decimal | null => {
    const ca = safeDecimal(currentAssets);
    const cl = safeDecimal(currentLiabilities);
    if (!ca || !cl) return null;
    return ca.minus(cl);
  },
};

// ===========================================
// Leverage Ratios
// ===========================================

export const leverageRatios = {
  /**
   * Debt to Equity Ratio = Total Debt / Total Equity
   * Measures financial leverage
   * Benchmark: < 2.0 is generally acceptable
   */
  debtToEquity: (totalDebt: string, totalEquity: string): Decimal | null => {
    const debt = safeDecimal(totalDebt);
    const equity = safeDecimal(totalEquity);
    return safeDivide(debt, equity);
  },

  /**
   * Debt to Tangible Net Worth = Total Debt / (Total Equity - Intangibles)
   * More conservative leverage measure
   * Benchmark: < 3.0 for most industries
   */
  debtToTangibleNetWorth: (
    totalDebt: string,
    totalEquity: string,
    intangibles: string
  ): Decimal | null => {
    const debt = safeDecimal(totalDebt);
    const equity = safeDecimal(totalEquity);
    const intang = safeDecimal(intangibles) || new Decimal(0);
    if (!debt || !equity) return null;
    const tnw = equity.minus(intang);
    return safeDivide(debt, tnw);
  },

  /**
   * Debt Ratio = Total Debt / Total Assets
   * Shows proportion of assets financed by debt
   * Benchmark: < 0.6 (60%) is typically healthy
   */
  debtRatio: (totalDebt: string, totalAssets: string): Decimal | null => {
    const debt = safeDecimal(totalDebt);
    const assets = safeDecimal(totalAssets);
    return safeDivide(debt, assets);
  },

  /**
   * Equity Ratio = Total Equity / Total Assets
   * Shows proportion of assets financed by equity
   */
  equityRatio: (totalEquity: string, totalAssets: string): Decimal | null => {
    const equity = safeDecimal(totalEquity);
    const assets = safeDecimal(totalAssets);
    return safeDivide(equity, assets);
  },

  /**
   * Leverage Ratio (Financial Leverage) = Total Assets / Total Equity
   */
  leverageRatio: (totalAssets: string, totalEquity: string): Decimal | null => {
    const assets = safeDecimal(totalAssets);
    const equity = safeDecimal(totalEquity);
    return safeDivide(assets, equity);
  },
};

// ===========================================
// Profitability Ratios
// ===========================================

export const profitabilityRatios = {
  /**
   * Gross Profit Margin = (Gross Profit / Revenue) * 100
   * Measures efficiency of production
   */
  grossProfitMargin: (grossProfit: string, revenue: string): Decimal | null => {
    const gp = safeDecimal(grossProfit);
    const rev = safeDecimal(revenue);
    const ratio = safeDivide(gp, rev);
    return ratio ? ratio.times(100) : null;
  },

  /**
   * Net Profit Margin = (Net Income / Revenue) * 100
   * Measures overall profitability
   * Benchmark: varies by industry, > 5% is generally good
   */
  netProfitMargin: (netIncome: string, revenue: string): Decimal | null => {
    const ni = safeDecimal(netIncome);
    const rev = safeDecimal(revenue);
    const ratio = safeDivide(ni, rev);
    return ratio ? ratio.times(100) : null;
  },

  /**
   * Operating Margin = (Operating Income / Revenue) * 100
   * Measures operational efficiency
   */
  operatingMargin: (operatingIncome: string, revenue: string): Decimal | null => {
    const oi = safeDecimal(operatingIncome);
    const rev = safeDecimal(revenue);
    const ratio = safeDivide(oi, rev);
    return ratio ? ratio.times(100) : null;
  },

  /**
   * Return on Assets (ROA) = (Net Income / Total Assets) * 100
   * Measures asset efficiency
   */
  returnOnAssets: (netIncome: string, totalAssets: string): Decimal | null => {
    const ni = safeDecimal(netIncome);
    const assets = safeDecimal(totalAssets);
    const ratio = safeDivide(ni, assets);
    return ratio ? ratio.times(100) : null;
  },

  /**
   * Return on Equity (ROE) = (Net Income / Total Equity) * 100
   * Measures return to shareholders
   * Benchmark: > 15% is generally good
   */
  returnOnEquity: (netIncome: string, totalEquity: string): Decimal | null => {
    const ni = safeDecimal(netIncome);
    const equity = safeDecimal(totalEquity);
    const ratio = safeDivide(ni, equity);
    return ratio ? ratio.times(100) : null;
  },

  /**
   * EBITDA = Operating Income + Depreciation + Amortization
   * Measures operating performance before non-cash items
   */
  ebitda: (
    operatingIncome: string,
    depreciation: string,
    amortization: string
  ): Decimal | null => {
    const oi = safeDecimal(operatingIncome);
    const dep = safeDecimal(depreciation) || new Decimal(0);
    const amort = safeDecimal(amortization) || new Decimal(0);
    if (!oi) return null;
    return oi.plus(dep).plus(amort);
  },

  /**
   * EBITDAR = EBITDA + Rent
   * Used for businesses with significant lease obligations
   */
  ebitdar: (ebitda: Decimal, rent: string): Decimal | null => {
    const r = safeDecimal(rent) || new Decimal(0);
    return ebitda.plus(r);
  },
};

// ===========================================
// Coverage Ratios
// ===========================================

export const coverageRatios = {
  /**
   * Interest Coverage Ratio = EBIT / Interest Expense
   * Measures ability to pay interest
   * Benchmark: > 3.0 is healthy
   */
  interestCoverage: (ebit: string, interest: string): Decimal | null => {
    const e = safeDecimal(ebit);
    const i = safeDecimal(interest);
    return safeDivide(e, i);
  },

  /**
   * Debt Service Coverage Ratio (DSCR) = EBITDA / (Interest + Principal)
   * Measures ability to service debt
   * Benchmark: > 1.25 is typically required by lenders
   */
  debtServiceCoverage: (
    ebitda: Decimal,
    interest: string,
    principal: string
  ): Decimal | null => {
    const i = safeDecimal(interest) || new Decimal(0);
    const p = safeDecimal(principal) || new Decimal(0);
    const debtService = i.plus(p);
    return safeDivide(ebitda, debtService);
  },

  /**
   * Fixed Charge Coverage Ratio = EBITDAR / (Interest + Rent + Tax-adjusted Principal)
   * More comprehensive coverage measure
   * Benchmark: > 1.2 is typically required
   */
  fixedChargeCoverage: (
    ebitdar: Decimal,
    interest: string,
    rent: string,
    principal: string,
    taxRate: number = 0.35
  ): Decimal | null => {
    const i = safeDecimal(interest) || new Decimal(0);
    const r = safeDecimal(rent) || new Decimal(0);
    const p = safeDecimal(principal) || new Decimal(0);
    // Tax-adjusted principal: Principal / (1 - Tax Rate)
    const taxAdjustedPrincipal = p.div(1 - taxRate);
    const fixedCharges = i.plus(r).plus(taxAdjustedPrincipal);
    return safeDivide(ebitdar, fixedCharges);
  },

  /**
   * Cash Flow Coverage = Operating Cash Flow / Total Debt
   * Measures ability to pay off debt from operations
   */
  cashFlowCoverage: (operatingCashFlow: string, totalDebt: string): Decimal | null => {
    const ocf = safeDecimal(operatingCashFlow);
    const debt = safeDecimal(totalDebt);
    return safeDivide(ocf, debt);
  },
};

// ===========================================
// Activity Ratios
// ===========================================

export const activityRatios = {
  /**
   * Inventory Turnover = Cost of Goods Sold / Average Inventory
   * Measures inventory efficiency
   */
  inventoryTurnover: (cogs: string, avgInventory: string): Decimal | null => {
    const c = safeDecimal(cogs);
    const inv = safeDecimal(avgInventory);
    return safeDivide(c, inv);
  },

  /**
   * Days Inventory Outstanding = 365 / Inventory Turnover
   * Average days to sell inventory
   */
  daysInventory: (cogs: string, avgInventory: string): Decimal | null => {
    const turnover = activityRatios.inventoryTurnover(cogs, avgInventory);
    if (!turnover || turnover.isZero()) return null;
    return new Decimal(365).div(turnover);
  },

  /**
   * Receivables Turnover = Net Credit Sales / Average Receivables
   * Measures collection efficiency
   */
  receivablesTurnover: (sales: string, avgReceivables: string): Decimal | null => {
    const s = safeDecimal(sales);
    const ar = safeDecimal(avgReceivables);
    return safeDivide(s, ar);
  },

  /**
   * Days Sales Outstanding (DSO) = (Average Receivables / Sales) * 365
   * Average collection period
   * Benchmark: < 45 days is generally good
   */
  daysSalesOutstanding: (avgReceivables: string, sales: string): Decimal | null => {
    const ar = safeDecimal(avgReceivables);
    const s = safeDecimal(sales);
    const ratio = safeDivide(ar, s);
    return ratio ? ratio.times(365) : null;
  },

  /**
   * Payables Turnover = Purchases / Average Payables
   */
  payablesTurnover: (purchases: string, avgPayables: string): Decimal | null => {
    const p = safeDecimal(purchases);
    const ap = safeDecimal(avgPayables);
    return safeDivide(p, ap);
  },

  /**
   * Days Payables Outstanding = 365 / Payables Turnover
   * Average payment period
   */
  daysPayables: (purchases: string, avgPayables: string): Decimal | null => {
    const turnover = activityRatios.payablesTurnover(purchases, avgPayables);
    if (!turnover || turnover.isZero()) return null;
    return new Decimal(365).div(turnover);
  },

  /**
   * Asset Turnover = Revenue / Total Assets
   * Measures asset efficiency
   */
  assetTurnover: (revenue: string, totalAssets: string): Decimal | null => {
    const rev = safeDecimal(revenue);
    const assets = safeDecimal(totalAssets);
    return safeDivide(rev, assets);
  },

  /**
   * Cash Conversion Cycle = DIO + DSO - DPO
   * Days to convert inventory to cash
   */
  cashConversionCycle: (dio: Decimal, dso: Decimal, dpo: Decimal): Decimal => {
    return dio.plus(dso).minus(dpo);
  },
};

// ===========================================
// Cash Flow Ratios
// ===========================================

export const cashFlowRatios = {
  /**
   * Traditional Cash Flow = Net Income + Depreciation + Amortization
   */
  traditionalCashFlow: (
    netIncome: string,
    depreciation: string,
    amortization: string
  ): Decimal | null => {
    const ni = safeDecimal(netIncome);
    const dep = safeDecimal(depreciation) || new Decimal(0);
    const amort = safeDecimal(amortization) || new Decimal(0);
    if (!ni) return null;
    return ni.plus(dep).plus(amort);
  },

  /**
   * Operating Cash Flow Ratio = Operating Cash Flow / Current Liabilities
   * Measures ability to cover short-term obligations
   */
  operatingCashFlowRatio: (
    operatingCashFlow: string,
    currentLiabilities: string
  ): Decimal | null => {
    const ocf = safeDecimal(operatingCashFlow);
    const cl = safeDecimal(currentLiabilities);
    return safeDivide(ocf, cl);
  },

  /**
   * Free Cash Flow = Operating Cash Flow - CapEx
   */
  freeCashFlow: (operatingCashFlow: string, capex: string): Decimal | null => {
    const ocf = safeDecimal(operatingCashFlow);
    const cx = safeDecimal(capex) || new Decimal(0);
    if (!ocf) return null;
    return ocf.minus(cx);
  },
};

// ===========================================
// Altman Z-Score (Bankruptcy Prediction)
// ===========================================

/**
 * Altman Z-Score for Manufacturing Companies
 * Z = 1.2X1 + 1.4X2 + 3.3X3 + 0.6X4 + 1.0X5
 * Where:
 * X1 = Working Capital / Total Assets
 * X2 = Retained Earnings / Total Assets
 * X3 = EBIT / Total Assets
 * X4 = Market Value of Equity / Total Liabilities
 * X5 = Sales / Total Assets
 *
 * Interpretation:
 * Z > 2.99: Safe Zone
 * 1.81 < Z < 2.99: Grey Zone
 * Z < 1.81: Distress Zone
 */
export function altmanZScore(
  workingCapital: string,
  retainedEarnings: string,
  ebit: string,
  marketValueEquity: string,
  totalLiabilities: string,
  sales: string,
  totalAssets: string
): Decimal | null {
  const wc = safeDecimal(workingCapital);
  const re = safeDecimal(retainedEarnings);
  const eb = safeDecimal(ebit);
  const mve = safeDecimal(marketValueEquity);
  const tl = safeDecimal(totalLiabilities);
  const s = safeDecimal(sales);
  const ta = safeDecimal(totalAssets);

  if (!ta || ta.isZero() || !tl || tl.isZero()) return null;

  const x1 = wc ? wc.div(ta) : new Decimal(0);
  const x2 = re ? re.div(ta) : new Decimal(0);
  const x3 = eb ? eb.div(ta) : new Decimal(0);
  const x4 = mve ? mve.div(tl) : new Decimal(0);
  const x5 = s ? s.div(ta) : new Decimal(0);

  return x1
    .times(1.2)
    .plus(x2.times(1.4))
    .plus(x3.times(3.3))
    .plus(x4.times(0.6))
    .plus(x5.times(1.0));
}

// ===========================================
// Calculate All Ratios
// ===========================================

export interface AllRatiosResult {
  liquidity: {
    currentRatio: Decimal | null;
    quickRatio: Decimal | null;
    workingCapital: Decimal | null;
  };
  leverage: {
    debtToEquity: Decimal | null;
    debtToTangibleNetWorth: Decimal | null;
    debtRatio: Decimal | null;
  };
  profitability: {
    grossProfitMargin: Decimal | null;
    netProfitMargin: Decimal | null;
    operatingMargin: Decimal | null;
    returnOnAssets: Decimal | null;
    returnOnEquity: Decimal | null;
    ebitda: Decimal | null;
  };
  coverage: {
    interestCoverage: Decimal | null;
    debtServiceCoverage: Decimal | null;
    fixedChargeCoverage: Decimal | null;
  };
  activity: {
    inventoryTurnover: Decimal | null;
    daysInventory: Decimal | null;
    receivablesTurnover: Decimal | null;
    daysSalesOutstanding: Decimal | null;
    assetTurnover: Decimal | null;
  };
  cashFlow: {
    traditionalCashFlow: Decimal | null;
  };
}

export function calculateAllRatios(inputs: FinancialCalculationInputs): AllRatiosResult {
  // Calculate EBITDA first as it's used in other calculations
  const ebitda = profitabilityRatios.ebitda(
    inputs.operatingIncome || '0',
    inputs.depreciation || '0',
    inputs.amortization || '0'
  );

  const ebitdar = ebitda
    ? profitabilityRatios.ebitdar(ebitda, inputs.rent || '0')
    : null;

  return {
    liquidity: {
      currentRatio: liquidityRatios.currentRatio(
        inputs.currentAssets || '',
        inputs.currentLiabilities || ''
      ),
      quickRatio: liquidityRatios.quickRatio(
        inputs.currentAssets || '',
        inputs.inventory || '0',
        inputs.currentLiabilities || ''
      ),
      workingCapital: liquidityRatios.workingCapital(
        inputs.currentAssets || '',
        inputs.currentLiabilities || ''
      ),
    },
    leverage: {
      debtToEquity: leverageRatios.debtToEquity(
        inputs.totalDebt || '',
        inputs.totalEquity || ''
      ),
      debtToTangibleNetWorth: leverageRatios.debtToTangibleNetWorth(
        inputs.totalDebt || '',
        inputs.totalEquity || '',
        inputs.intangibles || '0'
      ),
      debtRatio: leverageRatios.debtRatio(
        inputs.totalDebt || '',
        inputs.totalAssets || ''
      ),
    },
    profitability: {
      grossProfitMargin: profitabilityRatios.grossProfitMargin(
        inputs.grossProfit || '',
        inputs.revenue || ''
      ),
      netProfitMargin: profitabilityRatios.netProfitMargin(
        inputs.netIncome || '',
        inputs.revenue || ''
      ),
      operatingMargin: profitabilityRatios.operatingMargin(
        inputs.operatingIncome || '',
        inputs.revenue || ''
      ),
      returnOnAssets: profitabilityRatios.returnOnAssets(
        inputs.netIncome || '',
        inputs.totalAssets || ''
      ),
      returnOnEquity: profitabilityRatios.returnOnEquity(
        inputs.netIncome || '',
        inputs.totalEquity || ''
      ),
      ebitda,
    },
    coverage: {
      interestCoverage: coverageRatios.interestCoverage(
        inputs.ebit || inputs.operatingIncome || '',
        inputs.interest || ''
      ),
      debtServiceCoverage: ebitda
        ? coverageRatios.debtServiceCoverage(
            ebitda,
            inputs.interest || '0',
            inputs.principal || '0'
          )
        : null,
      fixedChargeCoverage: ebitdar
        ? coverageRatios.fixedChargeCoverage(
            ebitdar,
            inputs.interest || '0',
            inputs.rent || '0',
            inputs.principal || '0'
          )
        : null,
    },
    activity: {
      inventoryTurnover: activityRatios.inventoryTurnover(
        inputs.cogs || '',
        inputs.avgInventory || ''
      ),
      daysInventory: activityRatios.daysInventory(
        inputs.cogs || '',
        inputs.avgInventory || ''
      ),
      receivablesTurnover: activityRatios.receivablesTurnover(
        inputs.sales || inputs.revenue || '',
        inputs.avgReceivables || ''
      ),
      daysSalesOutstanding: activityRatios.daysSalesOutstanding(
        inputs.avgReceivables || '',
        inputs.sales || inputs.revenue || ''
      ),
      assetTurnover: activityRatios.assetTurnover(
        inputs.revenue || '',
        inputs.totalAssets || ''
      ),
    },
    cashFlow: {
      traditionalCashFlow: cashFlowRatios.traditionalCashFlow(
        inputs.netIncome || '',
        inputs.depreciation || '0',
        inputs.amortization || '0'
      ),
    },
  };
}

// ===========================================
// Format Helpers
// ===========================================

export function formatRatio(value: Decimal | null, decimals: number = 2): string {
  if (!value) return 'N/A';
  return value.toFixed(decimals);
}

export function formatPercentage(value: Decimal | null, decimals: number = 1): string {
  if (!value) return 'N/A';
  return `${value.toFixed(decimals)}%`;
}

export function formatCurrency(value: Decimal | null, decimals: number = 0): string {
  if (!value) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value.toNumber());
}

// ===========================================
// Ratio Benchmarks & Interpretation
// ===========================================

export const ratioBenchmarks = {
  currentRatio: { min: 1.0, ideal: 2.0, max: 3.0 },
  quickRatio: { min: 0.8, ideal: 1.5, max: 2.5 },
  debtToEquity: { min: 0, ideal: 1.0, max: 2.0 },
  debtToTangibleNetWorth: { min: 0, ideal: 1.5, max: 3.0 },
  interestCoverage: { min: 1.5, ideal: 3.0, max: null },
  fixedChargeCoverage: { min: 1.0, ideal: 1.25, max: null },
  netProfitMargin: { min: 0, ideal: 10, max: null },
  returnOnEquity: { min: 10, ideal: 20, max: null },
  daysSalesOutstanding: { min: null, ideal: 30, max: 45 },
};

export function getRatioStatus(
  ratioName: string,
  value: Decimal | null
): 'good' | 'acceptable' | 'concerning' | 'critical' | 'unknown' {
  if (!value) return 'unknown';

  const benchmark = ratioBenchmarks[ratioName as keyof typeof ratioBenchmarks];
  if (!benchmark) return 'unknown';

  const num = value.toNumber();

  // For ratios where higher is better
  if (ratioName === 'currentRatio' || ratioName === 'quickRatio') {
    if (num >= benchmark.ideal!) return 'good';
    if (num >= benchmark.min!) return 'acceptable';
    if (num >= benchmark.min! * 0.7) return 'concerning';
    return 'critical';
  }

  // For coverage ratios (higher is better)
  if (ratioName === 'interestCoverage' || ratioName === 'fixedChargeCoverage') {
    if (num >= benchmark.ideal!) return 'good';
    if (num >= benchmark.min!) return 'acceptable';
    if (num >= 1.0) return 'concerning';
    return 'critical';
  }

  // For debt ratios (lower is better)
  if (ratioName === 'debtToEquity' || ratioName === 'debtToTangibleNetWorth') {
    if (num <= benchmark.ideal!) return 'good';
    if (num <= benchmark.max!) return 'acceptable';
    if (num <= benchmark.max! * 1.5) return 'concerning';
    return 'critical';
  }

  // For DSO (lower is better)
  if (ratioName === 'daysSalesOutstanding') {
    if (num <= benchmark.ideal!) return 'good';
    if (num <= benchmark.max!) return 'acceptable';
    if (num <= benchmark.max! * 1.5) return 'concerning';
    return 'critical';
  }

  return 'unknown';
}

